import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, getApiHost } from '../utils/api';

type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

interface Props {
  currentUsername: string | null;
  onSaved: (newUsername: string) => void;
}

/**
 * Inline username editor for the Account Information card. Matches
 * the display-name field style. Debounced availability check fires
 * ~400ms after the user stops typing; invalid formats are rejected
 * locally without hitting the server.
 */
const UsernameField: React.FC<Props> = ({ currentUsername, onSaved }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [input, setInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<AvailabilityStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);

  const authedHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('deepnets_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  // Reset the editor whenever the stored value changes (e.g. after save).
  useEffect(() => {
    setInput(currentUsername ?? '');
  }, [currentUsername]);

  // Debounced availability check. Skips the server roundtrip if the
  // input is empty or equals the current username.
  useEffect(() => {
    if (!isEditing) return;
    const trimmed = input.trim();

    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    if (!trimmed) {
      setStatus('idle');
      setMessage(null);
      return;
    }
    if (trimmed.toLowerCase() === (currentUsername ?? '').toLowerCase()) {
      setStatus('idle');
      setMessage(null);
      return;
    }

    setStatus('checking');
    setMessage(null);

    debounceRef.current = window.setTimeout(async () => {
      try {
        const res = await apiFetch(
          `${getApiHost()}/api/auth/username/available?q=${encodeURIComponent(trimmed)}`
        );
        const data = await res.json();
        if (data.available) {
          setStatus('available');
          setMessage(null);
        } else if (data.reason === 'taken') {
          setStatus('taken');
          setMessage('Taken');
        } else {
          setStatus('invalid');
          setMessage(data.message || 'Invalid username');
        }
      } catch {
        setStatus('idle');
        setMessage(null);
      }
    }, 400) as unknown as number;

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [input, isEditing, currentUsername]);

  const canSave = isEditing && input.trim().length >= 3 && (status === 'available' || status === 'idle') && !isSaving;

  const handleSave = async () => {
    const normalized = input.trim().toLowerCase();
    if (!normalized) return;
    setIsSaving(true);
    setMessage(null);
    try {
      const res = await apiFetch(`${getApiHost()}/api/auth/username`, {
        method: 'POST',
        headers: authedHeaders(),
        body: JSON.stringify({ username: normalized }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to set username');
      }
      const data = await res.json();
      onSaved(data.user?.username ?? normalized);
      setIsEditing(false);
      setStatus('idle');
      setMessage(null);
    } catch (err) {
      setStatus('invalid');
      setMessage(err instanceof Error ? err.message : 'Failed to set username');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInput(currentUsername ?? '');
    setStatus('idle');
    setMessage(null);
  };

  return (
    <div>
      <label className="block text-sm text-gray-400 mb-1">Username</label>
      {isEditing ? (
        <div>
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/\s+/g, ''))}
              maxLength={20}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="flex-1 bg-crypto-dark border border-gray-700 rounded-lg px-3 py-1.5 text-white placeholder-gray-500 focus:outline-none focus:border-crypto-accent transition-colors text-sm font-mono"
              placeholder="username"
            />
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="px-3 py-1.5 bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-medium rounded-lg transition-colors disabled:opacity-50 text-sm"
            >
              {isSaving ? '...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              ✕
            </button>
          </div>
          <div className="mt-1 min-h-[1em] text-xs">
            {status === 'checking' && (
              <span className="text-gray-500">Checking…</span>
            )}
            {status === 'available' && (
              <span className="text-green-400">Available</span>
            )}
            {status === 'taken' && (
              <span className="text-red-400">{message}</span>
            )}
            {status === 'invalid' && (
              <span className="text-red-400">{message}</span>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-white text-sm font-mono">
            {currentUsername ? `@${currentUsername}` : 'Not set'}
          </span>
          <button
            onClick={() => setIsEditing(true)}
            className="text-gray-400 hover:text-crypto-accent transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default UsernameField;
